import React, { forwardRef, useEffect, useState } from 'react';
import { isEmpty } from 'lodash-es';
import styles from '../index.module.scss';
import { Checkbox } from '@maxtropy/components';
import { EnergyMonitorContext } from '../../';
import { StateTimeType } from '../../utils';
import { Spin } from 'antd';
import {
  apiV2HighEnergyDeviceDeviceIndicatorItemPost,
  V2HighEnergyDeviceDeviceIndicatorItemPostResponse,
} from '@maxtropy/device-customer-apis-v2';
import { DeviceMonitorSingleValue, DeviceMonitorValue, SurveyFlatItem, SurveyFlatProps } from './DeviceCompare';
import useLatestRequest from '../../useLatestRequest';

export interface IObjectCompare {
  onIndicatorChange?: (list: DeviceMonitorSingleValue[] | undefined) => void;
}
export type IndicatorProps = Exclude<V2HighEnergyDeviceDeviceIndicatorItemPostResponse['list'], undefined>[number];
const MAX = 2;
const IndicatorCompare = (props: IObjectCompare, ref: any) => {
  const { onIndicatorChange } = props;
  const [checkedList, setCheckedList] = useState<DeviceMonitorValue[]>([]);
  const [indicatorOp, setIndicatorOp] = useState<SurveyFlatProps>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const { deviceLedgerId } = React.useContext(EnergyMonitorContext);
  const { runLatest, cancelLatest } = useLatestRequest();

  useEffect(() => {
    if (!deviceLedgerId) {
      cancelLatest();
      setIndicatorOp([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    runLatest(() => apiV2HighEnergyDeviceDeviceIndicatorItemPost({ inventoryIds: [deviceLedgerId] }), {
      onSuccess: res => {
        const option: SurveyFlatProps = (res.list ?? []).flatMap(item => {
          if (item.type === StateTimeType.ENERGY && item.energyMediumItem) {
            return item.energyMediumItem.map(d => ({
              key: `${item.type}-${d.id}`,
              label: d.name ?? '',
              value: { type: item.type!, id: d.id, measurementType: d.measurementType },
            }));
          }

          if (item.type === StateTimeType.MONITORING && item.groupIndicator) {
            return (item.groupIndicator ?? []).flatMap<SurveyFlatItem>(d => {
              if (d.groupType !== 0) {
                const ids = (d.propertyList ?? []).map(p => p.propertyId!);
                return [
                  {
                    key: `${item.type}-${ids[0]}`,
                    label: d.showName ?? '',
                    value: {
                      type: item.type!,
                      id: ids,
                      measurementType: d?.propertyList?.[0]?.measurementType,
                    },
                  },
                ];
              } else {
                return (
                  d.propertyList?.map(p => ({
                    key: `${item.type}-${p.propertyId}`,
                    label: p.propertyName ?? '',
                    value: { type: item.type!, id: p.propertyId, measurementType: p.measurementType },
                  })) ?? []
                );
              }
            });
          }
          return {
            key: `${item.type}`,
            label: item.name ?? '',
            value: { type: item.type!, id: undefined },
          };
        });

        setIndicatorOp(option);
      },
      onSettled: () => setLoading(false),
    });
  }, [cancelLatest, deviceLedgerId, runLatest]);

  const onChange = (list: DeviceMonitorValue[]) => {
    if (list.length > MAX) return;
    setCheckedList(list);

    const processedData: DeviceMonitorSingleValue[] = list.reduce<DeviceMonitorSingleValue[]>((result, item) => {
      if (Array.isArray(item.id)) {
        item.id.forEach(id => {
          result.push({ type: item.type, id: id ?? null, measurementType: item?.measurementType });
        });
      } else {
        result.push(item as DeviceMonitorSingleValue);
      }
      return result;
    }, []);

    !isEmpty(list) ? onIndicatorChange?.(processedData) : onIndicatorChange?.(undefined);
  };

  return (
    <div className={styles.compareSection}>
      <Spin spinning={loading}>
        <div className="section device-section">
          <p className={styles.sectionTitle}>
            选择监控指标 <span className="moreNumber">最多选择2个</span>
          </p>
          <div className={styles.monitorList}>
            <Checkbox.Group onChange={onChange} layout="vertical" defaultValue={checkedList}>
              {indicatorOp?.map(i => (
                <Checkbox
                  key={i.key}
                  value={i.value}
                  disabled={
                    checkedList.length >= MAX && !checkedList.some(v => JSON.stringify(v) === JSON.stringify(i.value))
                  }
                >
                  {i.label}
                </Checkbox>
              ))}
            </Checkbox.Group>
          </div>
        </div>
      </Spin>
    </div>
  );
};

export default forwardRef(IndicatorCompare);
